import { html, Page, rune, View } from 'rune-ts';
import { BannerTypeS } from '../../../features/Banner/outbound/share';
import { EXCLUDE_MAKER_MODULE } from '../../../shared/constant';
import {
  CreatorRankingMain,
  CreatorRankingMainProps,
} from '../../components/cells/CreatorRankingMain/CreatorRankingMain';
import { footer_links } from '../../components/cells/Footer/constant';
import { Header } from '../../components/cells/Header/Global/Header.ts';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { QuickMenuData, QuickMenuListView } from '../../components/cells/QuickMenu/QuickMenu';
import homeClass from './Main.module.scss';
import { CategoryProductRankingView } from '@services/MarppleShop/renderApp/pages/Main/views/CategoryProductRankingView/CategoryProductRankingView.ts';
import { CastOnAir, CastOnAirProps } from './views/CastOnAir/CastOnAir';
import { LimitedEdition, LimitedEditionProps } from './views/LimitedEdition/LimitedEdition';
import { MainBannerView } from './views/MainBanner/MainBanner';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { ClientPopupProps, Popup } from '../../../shared/components/cells/Popup/Popup';
import {
  RecentlySoldProductSection,
  RecentlySoldProductSectionData,
} from './views/RecentlySoldProductSection/RecentlySoldProductSection';
import { htmlIf } from '../../../shared/util';
import { ProductCardData } from '../../../features/ProductList/type';
import { FloatingButton_recent_product } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product';
import { FloatingBox } from '../../components/cells/FloatingBox/FloatingBox';
import { CreatorRankingList, CreatorRankingListProps } from './views/CreatorRankingList/CreatorRankingList';
import {
  BrandCommerceSectionData,
  BrandCommerceSectionView,
} from '@services/MarppleShop/renderApp/pages/Main/views/BrandCommerceSection/BrandCommerceSection.ts';
import { generateProductRankingUrl } from '@services/MarppleShop/renderApp/pages/ProductRanking/utils/generateProductRankingUrl.ts';
import { makeShopScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { PopularTopNProductSectionView } from '@services/MarppleShop/renderApp/pages/Main/views/PopularTopNProductSection/PopularTopNProductSection.ts';
import { LegacyCateList } from '@services/MarppleShop/features/CateList/type';
import { ICreatorRanking } from '@services/MarppleShop/features/Creator/type';
import { NewCreatorRankingSection } from '@services/MarppleShop/renderApp/components/cells/NewCreatorRanking/NewCreatorRankingCard.ts';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';
import { generateProductListUrl } from '@services/MarppleShop/features/ProductList/util';

export interface HomePageData {
  banners: BannerTypeS.MainBannerData[];
  quick_menus: QuickMenuData[];
  creator_ranking: CreatorRankingMainProps;
  limited_edition: LimitedEditionProps;
  realtime_best: {
    products: ProductCardData[];
  };
  weekly_best: {
    products: ProductCardData[];
  };
  cast_on_air: CastOnAirProps;
  layout_options: BaseLayoutOptions;
  popup: ClientPopupProps;
  recently_sold: RecentlySoldProductSectionData;
  creator_ranking_list_props: CreatorRankingListProps;
  recent_product?: ProductCardData;
  brand_commerce: BrandCommerceSectionData;
  cate_lists: LegacyCateList[];
  adult_check_needed: boolean;

  /**
   * 이달의 신규 크리에이터
   */
  new_creator: {
    monthly_top_ranks: ICreatorRanking[];
  };

  readonly query: {
    cate_list_id?: number;
  };
}

class MainBodyView extends View<HomePageData> {
  override onMount() {
    const header_view = rune.getView(document.querySelector('.Header') as HTMLElement, Header);
    if (header_view) {
      header_view.redrawBgToWhiteWhenScroll();
    }
  }

  protected override template() {
    const {
      banners,
      weekly_best,
      quick_menus,
      creator_ranking,
      limited_edition,
      cast_on_air,
      popup,
      recently_sold,
      creator_ranking_list_props,
      brand_commerce,
    } = this.data;
    const is_mobile = this.data.layout_options.is_mobile;

    return html`<div class="${homeClass.container} ${EXCLUDE_MAKER_MODULE}">
      <div class="${homeClass.bannerContainer}">${new MainBannerView(banners)}</div>

      <div data-is-main="true" class="${homeClass.homeContainer}">
        <div class="${homeClass.quick_menu_wrap}">
          ${new QuickMenuListView(quick_menus, {
            size: is_mobile ? 'small' : 'medium',
            use_scroll_snap: is_mobile,
          })}
        </div>

        <!-- 실시간 크리에이터 랭킹 -->
        ${creator_ranking.creator_rankings.length > 0 ? new CreatorRankingMain(creator_ranking) : ''}

        <!-- 마플샵 인기 상품 Top 100 -->
        ${this.data.realtime_best.products.length > 0
          ? new PopularTopNProductSectionView(
              {
                section_title: {
                  title: ET('mps2::main::top_100::title'),
                  sub_title: ET('mps2::main::top_100::sub_title'),
                  see_more_link: generateProductRankingUrl(ET.lang, { rank_type: 'realtime' }),
                  navigate: makeShopScreenNavigate(
                    generateProductRankingUrl(ET.lang, { rank_type: 'realtime' }),
                  ),
                },
                products: this.data.realtime_best.products,
              },
              { is_mobile },
            )
          : ''}

        <!-- 방금, 막 판매된 실시간 판매 상품 -->
        ${htmlIf(
          new RecentlySoldProductSection(recently_sold, { is_mobile }),
          RecentlySoldProductSection.isVisible(recently_sold),
        )}

        <!-- 주간 크리에이터 랭킹 -->
        ${creator_ranking_list_props.creator_rankings.length > 0
          ? new CreatorRankingList(creator_ranking_list_props)
          : ''}

        <!-- 마플샵 한정 상품 랭킹 -->
        ${limited_edition.products.length > 0
          ? new LimitedEdition(
              limited_edition,
              { is_mobile },
              {
                getTitle: () => ET('mps2::main::limited_edition::title'),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                getSubTitle: () => {
                  return limited_edition.count
                    ? ET('mps2::main::limited_edition::sub_title', {
                        count_text: commify(limited_edition.count),
                      })
                    : '';
                },
                getSeeMoreLink: () => {
                  return generateProductListUrl({
                    is_limited: true,
                    without_sold_out: true,
                  });
                },
                getNavigate: () => {
                  return makeShopScreenNavigate(
                    generateProductListUrl({
                      is_limited: true,
                      without_sold_out: true,
                    }),
                  );
                },
              },
            )
          : ''}
        <!-- 카테고리별 상품 랭킹 -->
        ${weekly_best.products.length > 0
          ? new CategoryProductRankingView(
              {
                section_title: {
                  title: ET('mps2::main::category_product_ranking::title'),
                  sub_title: ET('mps2::main::category_product_ranking::sub_title'),
                  see_more_link: generateProductRankingUrl(ET.lang, { rank_type: 'weekly' }),
                  navigate: makeShopScreenNavigate(
                    generateProductRankingUrl(ET.lang, { rank_type: 'weekly' }),
                  ),
                },
                cate_lists: this.data.cate_lists,
                products: weekly_best.products,
                adult_check_needed: this.data.adult_check_needed,
                query: this.data.query,
              },
              { is_mobile },
            )
          : ''}

        <!-- 크리에이터 x 브랜드 상품 -->
        ${BrandCommerceSectionView.isVisible(brand_commerce)
          ? new BrandCommerceSectionView(brand_commerce, { is_mobile })
          : ''}

        <!-- Cast on Air -->
        ${cast_on_air.lives.length > 0 ? new CastOnAir(cast_on_air) : ''}

        <!-- 이달의 신규 크리에이터 랭킹 -->
        ${this.data.new_creator.monthly_top_ranks.length > 0
          ? new NewCreatorRankingSection({
              creator_rankings: this.data.new_creator.monthly_top_ranks,
              is_mobile,
            })
          : ''}
      </div>

      ${popup.popups.length > 0 ? new Popup(popup) : ''}
    </div>`;
  }
}

export class MainPage extends Page<HomePageData> {
  protected override template() {
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new MainBodyView(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            require_top_arrow_button: true,
            full_width: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 0,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(this.data.recent_product),
              async renderHook(view: FloatingBox) {
                await FloatingBox.refreshRecentProductFloatingButton(view);
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
